import { captureScreen, ScreenCaptureError } from './screenCapture';
import { installErrorBuffer, readPageErrorContext, readVisibleAlerts } from './errorBuffer';
import { buildCustomParamsQuery } from '../helpers/customParams';
import {
  GD_CHAT_READY,
  GD_SEND_INITIAL,
  HOST_INITIAL_DEDUPE_MS,
  hostInitialKey,
  type HostInitialIntent
} from '../helpers/hostInitialMessage';
import { isBrowserSessionReady } from '../helpers/browserSessionReady';
import {
  LAUNCHER_SLOTS,
  WIDGET_CORNERS,
  launcherSlotCss,
  nearestLauncherSlot,
  nearestWidgetCorner,
  widgetCornerCss
} from '../helpers/launcherSlots';
import type { BotHeartbeatSource } from '../helpers/botHeartbeat';

const IFRAME_ID = 'getdemo-bot-player';
const BACKDROP_ID = 'getdemo-backdrop';
const GD_CHECK_PARENT = 'GD_BROWSER_CHECK_PARENT';
const GD_PARENT_CONFIRMED = 'GD_BROWSER_PARENT_CONFIRMED';
const HANDSHAKE_TIMEOUT = 1000;
// Player -> parent "I'm mounted" ping, and parent -> player live identify push (iframe embed).
const GD_PLAYER_READY = 'gd-ready';
const GD_IDENTIFY = 'gd-identify';
const GD_CAPTURE_CAPABILITY = 'gd-capture-capability';
const GD_REQUEST_CAPTURE_CAPABILITY = 'gd-request-capture-capability';
const GD_CAPTURE_SCREEN = 'gd-capture-screen';
const GD_CAPTURE_SCREEN_RESULT = 'gd-capture-screen-result';
// Player -> parent: no modo manual, o visitante fechou o chat (X, ESC, backdrop ou o próprio
// agente encerrando). O pai volta a moldura ao estado armado.
const GD_MANUAL_CLOSED = 'gd-manual-closed';

export default class BotWidgetManager {
  private static instance: BotWidgetManager;
  private overrideBotId?: string;
  private overrideAppUrl?: string;
  private overrideEmbedFormat?: string;
  /**
   * COMO O AGENTE APARECE. `auto` é o de sempre: o player se anuncia (bolha, barra, pop-up).
   * `manual` arma e espera — nada é pintado até a plataforma do cliente chamar `Getdemo.open()`,
   * e fechar devolve a moldura ao estado armado.
   *
   *        resolve elegibilidade
   *                 │
   *                 ▼
   *          monta ARMADO  ──open()──▶  REVELADO  ──fechar/close()──▶  ARMADO
   *       visibility:hidden            visível                      (conversa preservada:
   *       pointer-events:none          isEnabled:true                o iframe nunca é destruído)
   */
  private triggerMode: 'auto' | 'manual' = 'auto';
  /** No modo manual: a moldura está revelada agora? É a verdade de "o chat está aberto". */
  private manualRevealed = false;
  /**
   * O player já montou sua árvore React e escuta mensagens?
   *
   * O player LEGADO não posta `gd-ready` (só o chat v3 posta), então o sinal utilizável é o
   * primeiro payload de geometria que ele manda — que sai de um efeito, logo depois da montagem.
   * Um `open()` que chega antes disso fica pendente e é descarregado ali.
   */
  private playerReady = false;
  private pendingReveal = false;
  private chatReady = false;
  private pendingInitial: HostInitialIntent | null = null;
  private lastInitialKey = '';
  private lastInitialAt = 0;
  private initialSeq = 0;
  /**
   * Avisa o loader que o visitante fechou o chat, para ele emitir `Getdemo.on('chat')`.
   *
   * É callback e não import do emitter porque o manager também roda no player (bridge mode) e
   * fora do loader unificado — e esse acoplamento invertido é o que mantém o manager testável
   * sem subir o loader inteiro.
   */
  public onManualClose?: () => void;
  private iframe: HTMLIFrameElement | null = null;
  private backdrop: HTMLDivElement | null = null;
  private slideOverlay: HTMLDivElement | null = null;
  private slideClose: HTMLButtonElement | null = null;
  private slideContent: HTMLDivElement | null = null;
  private tableOverlay: HTMLDivElement | null = null;
  private tableContent: HTMLDivElement | null = null;
  private tableKeyHandler: ((e: KeyboardEvent) => void) | null = null;
  private browserClientReady = false;
  private browserSessionReadyPacket?: string;
  private closeBrowserTransport?: () => void;
  private bridgeListenerSetup = false;
  private embeddedHydrated = false;
  private cachedCustomParams: Record<string, string | number | boolean> | null = null;
  private seededCustomParams: Record<string, string | number | boolean> | null = null;
  /**
   * A INSTRUÇÃO DA APLICAÇÃO (`window.Getdemo.instruction`), semeada pelo embed unificado.
   *
   * Guardada SEPARADA de `seededCustomParams` de propósito: ela é instrução, não atributo, e
   * misturá-la ali a mandaria pelo caminho que instrui o modelo a NUNCA seguir o que está escrito.
   */
  private seededInstruction: string | null = null;
  /**
   * A página JÁ chamou `Getdemo.instruction(...)` alguma vez nesta montagem?
   *
   * Decide se o envelope carrega o campo `instruction`. Um host que nunca usou a feature não
   * anuncia nada e o backend segue herdando o que está na conexão (o que os turnos sintéticos
   * precisam); um host que usou e depois LIMPOU anuncia `null`, e é assim que a limpeza chega.
   */
  private instructionAnnounced = false;
  private heartbeatSource: BotHeartbeatSource = 'legacy';
  /**
   * O tamanho e o canto do que o `clip-path` deixa visível, quando o widget está recolhido.
   *
   * `null` quando não há recorte (chat aberto): aí a moldura inteira É o widget. Guardado a cada
   * payload porque o arrastar precisa saber onde o widget está, e desde que a moldura passou a ter
   * tamanho constante ela deixou de responder isso sozinha.
   */
  private collapsedVisual: {
    width: number;
    height: number;
    anchorTop: boolean;
    anchorLeft: boolean;
  } | null = null;

  static getInstance(): BotWidgetManager {
    if (!BotWidgetManager.instance) {
      BotWidgetManager.instance = new BotWidgetManager();
    }
    return BotWidgetManager.instance;
  }

  start(): void {
    // Host page (top-level): expose window.Getdemo.identify immediately, so a legacy iframe
    // embed needs only a Getdemo.identify(...) call — no manual stub. Runs synchronously on
    // script load, before init() defers to DOMContentLoaded, so calls placed right after the
    // <script> tag are captured. The player iframe (cross-window) keeps its own path.
    if (window.top === window.self) {
      this.ensureGetdemoStub();
      // Buffer de erros da página: precisa estar de pé ANTES do erro acontecer, então instala no
      // load e não na primeira captura. Só a página host — dentro do iframe do player não há
      // sistema de cliente para observar.
      installErrorBuffer();
    }

    const onReady = () => {
      const isIframe = window.top !== window.self;
      if (isIframe) {
        this.startIframe();
      } else {
        this.init();
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', onReady);
    } else {
      onReady();
    }
  }

  private startIframe(): void {
    let resolved = false;

    const onMessage = (event: MessageEvent) => {
      if (resolved) return;
      const { type, iframeId } = event.data || {};
      if (type === GD_PARENT_CONFIRMED && iframeId) {
        resolved = true;
        window.removeEventListener('message', onMessage);
        this.initBridgeMode(iframeId);
      }
    };

    window.addEventListener('message', onMessage);

    try {
      window.parent.postMessage({ type: GD_CHECK_PARENT }, '*');
    } catch {
      resolved = true;
      window.removeEventListener('message', onMessage);
      console.log('[GetDemo] Cannot communicate with parent. Running as standalone widget.');
      this.init();
      return;
    }

    setTimeout(() => {
      if (!resolved) {
        resolved = true;
        window.removeEventListener('message', onMessage);
        console.log('[GetDemo] No parent bot detected. Running as standalone widget.');
        this.init();
      }
    }, HANDSHAKE_TIMEOUT);
  }

  private initBridgeMode(iframeId: string): void {
    console.log(
      '[GetDemo] Running in bridge mode (parent detected). This iframe is now accessible to the bot.'
    );

    window.GetDemoBrowser?.initAsBridge(iframeId);
  }

  public hydrateExistingIframe(): boolean {
    if (this.iframe) return false;
    const existingIframe = document.getElementById(IFRAME_ID) as HTMLIFrameElement | null;
    if (!existingIframe) return false;

    this.iframe = existingIframe;
    this.embeddedHydrated = true;
    this.backdrop = document.getElementById(BACKDROP_ID) as HTMLDivElement | null;
    this.applyIframeSrc(existingIframe);
    this.setupParentWidthSync();
    this.setupMessageListener();
    // Do NOT init the browser client here. applyIframeSrc() just (re)navigated the iframe, so
    // the player (BotWidget) hasn't mounted yet and its 'gd-browser-ws-send' listener isn't
    // registered. Initializing now would post `session_ready` into a window with no listener
    // (dropped, never retried) and the server's waitForClient() would hang forever. Defer to the
    // lazy init in setupMessageListener (fires on the player's first message), exactly like the
    // widget path (init()). This is what makes `mode=iframe` embeds work like `mode=widget`.
    this.setupLateIdentify();
    return true;
  }

  public hydrateEmbeddedIframeIfPresent(): void {
    this.ensureGetdemoStub();
    const run = () => {
      this.setupBridgeHandshakeEarlyListener();
      this.hydrateExistingIframe();
    };
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', run);
    } else {
      run();
    }
  }

  private init(): void {
    this.setupBridgeHandshakeEarlyListener();

    if (this.embeddedHydrated || this.hydrateExistingIframe()) {
      return;
    }

    const scriptTag = this.findScriptTag();
    if (!scriptTag) {
      console.error('[GetDemo] Script tag with gd-bot-id not found.');
      return;
    }

    const botId = scriptTag.getAttribute('gd-bot-id') || this.overrideBotId;
    const appUrl = scriptTag.getAttribute('gd-app-url') || this.overrideAppUrl;

    if (!botId) {
      console.error('[GetDemo] gd-bot-id attribute is required.');
      return;
    }
    if (!appUrl) {
      console.error('[GetDemo] gd-app-url attribute is required.');
      return;
    }

    const customParamsQuery = buildCustomParamsQuery(this.readHostCustomParams());
    const botUrl = `${appUrl.replace(
      /\/$/,
      ''
    )}/b/${botId}?mode=${this.modeParam()}${customParamsQuery}&gdSource=${
      this.heartbeatSource
    }${this.guideActiveQuery()}${this.triggerQuery()}`;
    this.createElements(botUrl);
    this.setupParentWidthSync();
    this.setupMessageListener();
    // Also here, not only in the iframe-embed path (hydrateExistingIframe): setHostCustomParams
    // posts gd-identify ONCE and a player that hasn't mounted its listener yet drops it for good.
    // That is the "identify works sometimes" race — the visitor's data survives or not depending
    // on how fast the player loads. setupLateIdentify re-posts on the player's gd-ready, so the
    // seeded params always land.
    this.setupLateIdentify();
  }

  private setupBridgeHandshakeEarlyListener(): void {
    if (this.bridgeListenerSetup) return;
    this.bridgeListenerSetup = true;
    window.addEventListener('message', (event: MessageEvent) => {
      const data = event.data;
      if (data && typeof data === 'object' && data.type === GD_CHECK_PARENT && event.source) {
        const iframeId = crypto.randomUUID();
        (event.source as Window).postMessage({ type: GD_PARENT_CONFIRMED, iframeId }, '*');
      }
    });
  }

  public configure(
    botId?: string,
    appUrl?: string,
    embedFormat?: string,
    triggerMode?: string
  ): void {
    if (botId) this.overrideBotId = botId;
    if (appUrl) this.overrideAppUrl = appUrl;
    if (embedFormat) this.overrideEmbedFormat = embedFormat;
    // Só widget e launcher têm estado armado. O formato `iframe` é inline na página do cliente e
    // não tem o que esconder, então ignora o modo — decisão do refinamento de 02/10/2026.
    this.triggerMode = triggerMode === 'manual' && embedFormat !== 'iframe' ? 'manual' : 'auto';
    this.heartbeatSource = 'embed';
  }

  public isManual(): boolean {
    return this.triggerMode === 'manual';
  }

  // O `&trigger=manual` que o player lê para não se anunciar: o widget não desenha a bolha e o
  // launcher fica no seu `view: 'pending'` em vez de abrir o pop-up de boas-vindas.
  private triggerQuery(): string {
    return this.triggerMode === 'manual' ? '&trigger=manual' : '';
  }

  /**
   * Revela a moldura e manda o player abrir o chat. É o que `Getdemo.open()` executa.
   *
   * Sem `{ task }` nem `{ message }`, chamar com o chat já aberto é no-op (duplo clique no
   * botão do cliente). Com intenção, a primeira mensagem sai uma vez, só depois de o player
   * escutar e de a conversa já ter a abertura — um segundo clique idêntico dentro de 1s não
   * duplica. Retorna false quando não há moldura montada — é esse false que o loader traduz em
   * `unavailable` para o cliente poder esconder o próprio botão.
   */
  public reveal(intent?: HostInitialIntent): boolean {
    if (!this.iframe) return false;
    this.armInitial(intent);
    if (this.manualRevealed) {
      this.flushInitial();
      return true;
    }
    this.manualRevealed = true;
    this.iframe.style.visibility = 'visible';
    this.iframe.style.pointerEvents = 'auto';
    // O player pode ainda não escutar. Guardamos a intenção e o primeiro payload dele descarrega.
    if (!this.playerReady) {
      this.pendingReveal = true;
      return true;
    }
    this.postOpenToPlayer();
    this.flushInitial();
    return true;
  }

  /** Devolve a moldura ao estado armado. O iframe NÃO é destruído: a conversa sobrevive. */
  public conceal(): boolean {
    if (!this.iframe || !this.manualRevealed) return false;
    this.manualRevealed = false;
    this.pendingReveal = false;
    this.pendingInitial = null;
    this.lastInitialKey = '';
    this.lastInitialAt = 0;
    this.applyConcealedStyle();
    return true;
  }

  /** Fecha pelo lado do player (o `close()` público) e deixa o conceal para o payload de volta. */
  public closeChat(): boolean {
    if (!this.iframe || !this.manualRevealed) return false;
    this.iframe.contentWindow?.postMessage(
      {
        type: 'gd-avatar-resize-reposition',
        data: { isEnabled: false, gdHostCollapseIntent: true }
      },
      '*'
    );
    return this.conceal();
  }

  private postOpenToPlayer(): void {
    this.iframe?.contentWindow?.postMessage(
      { type: 'gd-avatar-resize-reposition', data: { isEnabled: true } },
      '*'
    );
  }

  private armInitial(intent?: HostInitialIntent): void {
    if (!intent || (!intent.task && !intent.message)) return;
    const key = hostInitialKey(intent);
    if (this.pendingInitial && hostInitialKey(this.pendingInitial) === key) return;
    if (this.lastInitialKey === key && Date.now() - this.lastInitialAt < HOST_INITIAL_DEDUPE_MS) {
      return;
    }
    this.pendingInitial = intent;
  }

  private flushInitial(): void {
    if (!this.pendingInitial || !this.playerReady || !this.chatReady || !this.iframe) return;
    const intent = this.pendingInitial;
    this.pendingInitial = null;
    this.lastInitialKey = hostInitialKey(intent);
    this.lastInitialAt = Date.now();
    this.initialSeq += 1;
    this.iframe.contentWindow?.postMessage(
      { type: GD_SEND_INITIAL, id: this.initialSeq, data: intent },
      '*'
    );
  }

  private markPlayerReady(): void {
    if (this.playerReady) return;
    this.playerReady = true;
    if (this.pendingReveal) {
      this.pendingReveal = false;
      this.postOpenToPlayer();
    }
    this.flushInitial();
  }

  /**
   * `visibility` + `pointer-events`, e não `opacity` nem `display`.
   *
   * `opacity` seria desfeito pelo próprio `handleResizeReposition`, que termina em
   * `style.opacity = '1'` a cada payload. `display: none` zeraria a caixa do iframe e o player
   * passaria a se medir errado (a armadilha do `innerWidth` dentro de iframe). `visibility`
   * mantém o layout, não é tocada por nenhum payload, e `pointer-events` garante que a área
   * recortada não roube clique da página do cliente.
   */
  private applyConcealedStyle(): void {
    if (!this.iframe) return;
    this.iframe.style.visibility = 'hidden';
    this.iframe.style.pointerEvents = 'none';
  }

  // The player ?mode the loader mounts. Only 'launcher' diverges; 'widget' and 'iframe'
  // (a manual-embed format) both stay on the floating-widget player here.
  private modeParam(): string {
    return this.overrideEmbedFormat === 'launcher' ? 'launcher' : 'widget';
  }

  // A guide is being triggered on this page when the host URL carries ?guideId= (added by
  // redirectToGuide and kept by GuideManager). We forward a flag into the player so the launcher
  // can suppress its auto-open pop-up — otherwise the welcome modal opens on top of the guide.
  private guideActiveQuery(): string {
    try {
      return new URLSearchParams(window.location.search).get('guideId') ? '&gdGuideActive=1' : '';
    } catch {
      return '';
    }
  }

  // True while the chat overlay is open. Used to avoid swapping the agent mid-conversation.
  public isWidgetOpen(): boolean {
    // No modo manual a verdade é explícita (`manualRevealed`) e não deduzida do backdrop: a
    // moldura armada não tem backdrop nenhum, e é esta função que impede `hide()`/`swapBot()` de
    // matarem uma conversa em andamento numa navegação SPA.
    if (this.triggerMode === 'manual') return this.manualRevealed;
    return Boolean(this.backdrop && this.backdrop.style.display !== 'none');
  }

  // Swaps the active agent in place (SPA route change) by reloading the iframe to the new
  // bot. No-op when the id is unchanged, the chat is open, or the widget isn't mounted yet.
  public swapBot(botId?: string, appUrl?: string, embedFormat?: string): boolean {
    if (!botId || botId === this.overrideBotId) return false;
    if (this.isWidgetOpen()) return false;
    if (appUrl) this.overrideAppUrl = appUrl;
    if (!this.iframe || !this.overrideAppUrl) return false;

    this.overrideBotId = botId;
    if (embedFormat) this.overrideEmbedFormat = embedFormat;
    const customParamsQuery = buildCustomParamsQuery(this.readHostCustomParams());
    this.iframe.src = `${this.overrideAppUrl.replace(
      /\/$/,
      ''
    )}/b/${botId}?mode=${this.modeParam()}${customParamsQuery}&gdSource=${
      this.heartbeatSource
    }${this.guideActiveQuery()}${this.triggerQuery()}`;
    this.closeBrowserTransport?.();
    this.browserClientReady = false;
    this.playerReady = false;
    this.chatReady = false;
    this.pendingInitial = null;
    this.lastInitialKey = '';
    this.lastInitialAt = 0;
    return true;
  }

  // True while the widget iframe is mounted in the DOM.
  public isMounted(): boolean {
    return Boolean(this.iframe);
  }

  // Tears the widget down so it stops showing (SPA route change to a page where no agent
  // is eligible). No-op when nothing is mounted; skipped while the chat is open so an active
  // conversation is never killed mid-navigation. Clears overrideBotId so a later start()/
  // swapBot remounts cleanly. Returns true when it actually removed a mounted widget.
  public hide(): boolean {
    if (!this.iframe && !this.backdrop) return false;
    if (this.isWidgetOpen()) return false;
    this.slideOverlay?.remove();
    this.slideOverlay = null;
    this.slideClose = null;
    this.slideContent = null;
    this.iframe?.remove();
    this.iframe = null;
    this.backdrop?.remove();
    this.backdrop = null;
    this.closeBrowserTransport?.();
    this.browserClientReady = false;
    this.overrideBotId = undefined;
    // O widget foi desmontado: a instrução semeada morre com ele e é re-semeada por `startBot`
    // quando ele voltar. (O `swapBot` NÃO limpa, pelo mesmo motivo que não limpa
    // `seededCustomParams`: é a mesma página, o mesmo usuário logado e a mesma instrução da
    // aplicação — zerar ali criaria a falha "a instrução some depois de navegar".)
    this.seededInstruction = null;
    this.instructionAnnounced = false;
    // A moldura foi desmontada: o estado manual morre com ela e é re-armado por `startBot`.
    this.manualRevealed = false;
    this.pendingReveal = false;
    this.playerReady = false;
    this.chatReady = false;
    this.pendingInitial = null;
    this.lastInitialKey = '';
    this.lastInitialAt = 0;
    return true;
  }

  private findScriptTag(): HTMLScriptElement | null {
    const scripts = document.querySelectorAll('script[gd-bot-id], script[gd-company-id]');
    return scripts.length > 0 ? (scripts[0] as HTMLScriptElement) : null;
  }

  private toPrimitiveMap(obj: Record<string, unknown>): Record<string, string | number | boolean> {
    const result: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        result[key] = value;
      }
    }
    return result;
  }

  private readIdentifyParams(): Record<string, unknown> | null {
    try {
      const getdemo = (window as any).Getdemo;
      const queue = getdemo && Array.isArray(getdemo.q) ? getdemo.q : [];
      let params: Record<string, unknown> | null = null;
      for (const entry of queue) {
        if (Array.isArray(entry) && entry[0] === 'identify') {
          const args = entry[1];
          const candidate = args && args[1];
          if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
            params = candidate as Record<string, unknown>;
          }
        }
      }
      return params;
    } catch {
      return null;
    }
  }

  // Seeds the visitor's identify customParams from the unified embed (getdemo.js). That loader
  // snapshots `window.Getdemo.identify(...)` BEFORE the SDK drains/replaces `window.Getdemo.q`,
  // but the floating-widget bot only starts after `await fetchConfig()` — so by the time
  // `readHostCustomParams` reads the queue here it is already empty, and the bot iframe URL would
  // carry no customParams (the agent then re-asks for known values, e.g. the condomínio code).
  // Bridging the snapshot in closes that gap. Idempotent + additive (merges); when the player is
  // already mounted it live-pushes a post-load identify so the floating widget (whose init() path
  // never wired setupLateIdentify) still receives a post-login identify.
  /**
   * Semeia (ou limpa) a instrução da aplicação. Espelha {@link setHostCustomParams}, incluindo o
   * live-push para um player JÁ montado — uma chamada pós-login precisa chegar sem recarregar o
   * iframe, senão a conversa em andamento morreria.
   *
   * O transporte é `postMessage`, nunca query string: o player só precisa da instrução na hora de
   * enviar mensagem (o flush do `gd-ready` chega bem antes), e `buildCustomParamsQuery` DESCARTA o
   * objeto inteiro acima de 6000 chars — o que apagaria junto a identidade do visitante.
   */
  public setHostInstruction(text: string | null | undefined): void {
    const next = typeof text === 'string' && text.trim() ? text.trim() : null;
    if (next === this.seededInstruction) return;
    this.seededInstruction = next;
    if (next !== null) this.instructionAnnounced = true;
    if (this.iframe) {
      this.postHostIdentity();
    }
  }

  /** A instrução vigente para o player. */
  private hostInstruction(): string | null {
    return this.seededInstruction;
  }

  /**
   * O envelope `gd-identify` com tudo que o host sabe sobre ESTE visitante: os customParams
   * mesclados e a instrução da aplicação, como campos IRMÃOS. Um envelope só — a instrução herda
   * de graça o flush do `gd-ready` que já conserta a corrida do identify.
   */
  private postHostIdentity(): void {
    const customParams = this.readHostCustomParams();
    if (!customParams && !this.instructionAnnounced) return;
    this.iframe?.contentWindow?.postMessage(
      {
        type: GD_IDENTIFY,
        ...(customParams ? { customParams } : {}),
        // Presente mesmo quando `null`: é assim que uma LIMPEZA (logout, troca de usuário) chega
        // ao player. O listener de lá distingue "campo ausente" de "campo null".
        ...(this.instructionAnnounced ? { instruction: this.hostInstruction() } : {})
      },
      '*'
    );
  }

  public setHostCustomParams(params: Record<string, unknown> | null | undefined): void {
    if (!params || typeof params !== 'object' || Array.isArray(params)) return;
    const map = this.toPrimitiveMap(params as Record<string, unknown>);
    if (Object.keys(map).length === 0) return;
    this.seededCustomParams = { ...(this.seededCustomParams || {}), ...map };
    // Reflect into the cache too, so a swapBot that fires before any queue read still carries it.
    this.cachedCustomParams = { ...(this.cachedCustomParams || {}), ...map };
    if (this.iframe) {
      this.postHostIdentity();
    }
  }

  // The host page's own origin (app.almahcondos.com.br, cond1.…, cond2.…). Read on the HOST
  // (this manager runs top-level), so it is the domain the visitor is actually on. Surfaced to
  // the bot as `gdHostOrigin` so a flow can build an absolute in-app link (e.g. an "Abrir X"
  // button) that points back at the SAME domain, without the customer wiring up any identify.
  // Guarded to a real http(s) origin — a file:// / sandboxed page yields "null" (a string).
  private hostOrigin(): string | null {
    try {
      const origin = window.location.origin;
      return /^https?:\/\//.test(origin) ? origin : null;
    } catch {
      return null;
    }
  }

  // The identify queue is drained/replaced by the SDK soon after load, so we MERGE every source of
  // "who" and cache the union: the params SEEDED by the unified embed ({@link setHostCustomParams}),
  // a live read of the queue (authoritative when still present), and the previously-cached union.
  // A later swapBot (SPA navigation) then still carries "who" into the new iframe instead of
  // rebuilding the URL from an empty queue. Precedence: host origin < cache < seed < live queue
  // (gdHostOrigin is a base default, so a customer identify field of the same name still wins).
  private readHostCustomParams(): Record<string, string | number | boolean> | null {
    const identifyParams = this.readIdentifyParams();
    const queueMap = identifyParams ? this.toPrimitiveMap(identifyParams) : {};
    const origin = this.hostOrigin();
    const merged: Record<string, string | number | boolean> = {
      ...(origin ? { gdHostOrigin: origin } : {}),
      ...(this.cachedCustomParams || {}),
      ...(this.seededCustomParams || {}),
      ...queueMap
    };
    if (Object.keys(merged).length === 0) return null;
    this.cachedCustomParams = merged;
    return merged;
  }

  // Iframe embed: the snippet ships the iframe with no src (data-gd-src), so we set the final
  // URL once — already carrying the visitor's customParams from Getdemo.identify — with no
  // reload/flash. Falls back to an existing src for legacy snippets without data-gd-src.
  private applyIframeSrc(iframe: HTMLIFrameElement): void {
    const baseSrc = iframe.getAttribute('data-gd-src') || iframe.getAttribute('src');
    if (!baseSrc) return;
    iframe.src = `${baseSrc}${buildCustomParamsQuery(
      this.readHostCustomParams()
    )}&gdSource=embed${this.guideActiveQuery()}${this.triggerQuery()}`;
  }

  // Creates the window.Getdemo identify/trigger queue stub when the host page didn't include
  // it. Lets a (legacy) iframe embed work with just a Getdemo.identify(...) call — no manual
  // bootstrap snippet. Mirrors the stub the installer snippet ships.
  private ensureGetdemoStub(): void {
    try {
      const w = window as any;
      const g = (w.Getdemo = w.Getdemo || { q: [] });
      if (typeof g.identify === 'function') return;
      g.c =
        g.c ||
        ((n: string) =>
          (...args: any[]) => {
            (g.q = g.q || []).push([n, args]);
          });
      g.identify = g.c('identify');
      g.trigger = g.c('trigger');
      // `open`/`close` do modo manual entram na MESMA fila: o botão do cliente pode ser clicado
      // antes do loader terminar de carregar, e sem isto o clique viraria um TypeError no console
      // dele e se perderia. A fila é drenada por `ensureChatSurface` no loader.
      g.open = g.c('open');
      g.close = g.c('close');
      // Instalações NOVAS já nascem com o método; as ANTIGAS continuam funcionando pela fila
      // genérica (`ensureInstructionSurface` drena `['instruction', args]` de `q`).
      g.instruction = g.c('instruction');
    } catch {
      /* window not available */
    }
  }

  // Late identify for the iframe embed: when the host calls Getdemo.identify(...) after load,
  // push the merged customParams into the player live (no reload — keeps the chat intact).
  // Eligibility stays as evaluated at load; this only enriches personalization + the lead.
  // The player posts GD_PLAYER_READY on mount so we can flush an identify that arrived first.
  private setupLateIdentify(): void {
    const postIdentify = () => {
      this.postHostIdentity();
    };

    window.addEventListener('message', (event: MessageEvent) => {
      if (event.source === this.iframe?.contentWindow && event.data?.type === GD_PLAYER_READY) {
        postIdentify();
      }
    });

    try {
      const g = (window as any).Getdemo;
      if (g && typeof g.identify === 'function' && !g.identify.__gdIframeWrapped) {
        const original = g.identify;
        const wrapped = function (this: unknown, ...args: any[]) {
          const result = original.apply(this, args);
          postIdentify();
          return result;
        };
        (wrapped as any).__gdIframeWrapped = true;
        g.identify = wrapped;
      }
    } catch {
      /* identify not available */
    }
  }

  private createElements(botUrl: string): void {
    this.backdrop = document.createElement('div');
    this.backdrop.id = BACKDROP_ID;
    Object.assign(this.backdrop.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      backgroundColor: 'rgba(0,0,0,0.5)',
      zIndex: '9998',
      display: 'none',
      pointerEvents: 'none'
    });

    this.iframe = document.createElement('iframe');
    this.iframe.id = IFRAME_ID;
    this.iframe.src = botUrl;
    this.iframe.allow = 'microphone';
    Object.assign(this.iframe.style, {
      position: 'fixed',
      zIndex: '9999',
      border: 'none',
      bottom: '0',
      right: '0',
      opacity: '0'
    });

    document.body.appendChild(this.backdrop);
    document.body.appendChild(this.iframe);

    // Modo manual: nasce ARMADO. O player é pré-carregado (a abertura é instantânea e a conversa
    // sobrevive a fechar/reabrir), mas invisível e sem receber clique até `Getdemo.open()`.
    if (this.triggerMode === 'manual') {
      this.applyConcealedStyle();
    }
  }

  private initBrowserClient(): void {
    if (!window.GetDemoBrowser || !this.iframe) return;

    const transport = this.createPostMessageTransport();
    window.GetDemoBrowser.initWithTransport({ transport });
    this.browserClientReady = true;
  }

  private createPostMessageTransport(): {
    send(data: string): void;
    onMessage(handler: (data: string) => void): void;
    close(): void;
  } {
    let messageHandler: ((data: string) => void) | null = null;
    let closed = false;

    const onBrowserWsReceive = (event: MessageEvent) => {
      if (closed || !this.iframe || event.source !== this.iframe.contentWindow) return;
      const { type, data } = event.data || {};
      if (type === 'gd-browser-ws-receive' && messageHandler) {
        messageHandler(data);
      }
    };

    window.addEventListener('message', onBrowserWsReceive);

    const close = () => {
      closed = true;
      window.removeEventListener('message', onBrowserWsReceive);
      messageHandler = null;
      if (this.closeBrowserTransport === close) {
        this.closeBrowserTransport = undefined;
        this.browserSessionReadyPacket = undefined;
        this.browserClientReady = false;
      }
    };
    this.closeBrowserTransport = close;
    this.browserSessionReadyPacket = undefined;

    return {
      send: (data: string) => {
        if (closed || this.closeBrowserTransport !== close) return;
        if (isBrowserSessionReady(data)) this.browserSessionReadyPacket = data;
        this.iframe?.contentWindow?.postMessage({ type: 'gd-browser-ws-send', data }, '*');
      },
      onMessage: (handler: (data: string) => void) => {
        messageHandler = handler;
      },
      close
    };
  }

  // The host's own viewport. Width has always been here; height is what lets a player that is NOT
  // full-viewport lay itself out against the page — inside a card-sized iframe `window.innerHeight`
  // is the card's own height, so the player can neither clamp a position to the page nor size
  // itself as a fraction of it without being told. Kept as one payload builder so the broadcast
  // and the on-demand reply can never drift apart.
  private parentViewportPayload(): { width: number; height: number } {
    return { width: window.innerWidth, height: window.innerHeight };
  }

  private setupParentWidthSync(): void {
    const sendParentWidth = () => {
      if (this.iframe?.contentWindow) {
        this.iframe.contentWindow.postMessage(
          { type: 'gd-parent-width', data: this.parentViewportPayload() },
          '*'
        );
      }
    };

    this.iframe?.addEventListener('load', sendParentWidth);
    window.addEventListener('resize', sendParentWidth);
  }

  private setupMessageListener(): void {
    window.addEventListener('message', async (event: MessageEvent) => {
      if (!this.iframe || event.source !== this.iframe.contentWindow) {
        this.handleOverlayIframeMessage(event);
        return;
      }

      const { type, data } = event.data || {};

      // Handshake de capacidade ANTES de inicializar o cliente de automação, de propósito: aquilo
      // depende de uma lib externa e, se ela falhar, o `throw` levaria junto a resposta abaixo — e
      // o visitante perderia o botão por causa de um problema que não é dele.
      //
      // O player só mostra a câmera se o HOST souber fotografar: um bundle antigo não responde e o
      // botão nunca aparece, em vez de aparecer e dar timeout a cada clique. Responde ao aviso de
      // montagem E a quem perguntar depois — uma superfície que remonta no meio da conversa
      // precisa poder perguntar de novo, senão o botão some para o resto da conversa.
      if (type === GD_PLAYER_READY || type === GD_REQUEST_CAPTURE_CAPABILITY) {
        this.iframe?.contentWindow?.postMessage(
          {
            type: GD_CAPTURE_CAPABILITY,
            data: { supported: typeof window.GetDemoBrowser?.captureScreenshot === 'function' }
          },
          '*'
        );
      }

      // Lazy-init the browser client ONLY on the player's explicit "I'm mounted" ping
      // (GD_PLAYER_READY). Triggering on any earlier message — e.g. a
      // CHECK_IF_PARENT_HAS_SCRIPT ownership/handshake probe posted by a getdemo copy
      // running INSIDE the player iframe before its React app mounts — posts
      // `session_ready` into a window whose `gd-browser-ws-send` listener is not
      // registered yet: the message is dropped and never retried, so the server's
      // waitForClient() hangs forever. gd-ready only round-trips here AFTER mount, by
      // which point the player's listener is registered.
      if (type === GD_PLAYER_READY) {
        this.markPlayerReady();
        if (!this.browserClientReady) {
          this.initBrowserClient();
        } else if (this.browserSessionReadyPacket) {
          // Reloading/remounting the player loses its local cache, while the host SDK lives on.
          // Replay its original handshake, never instantiate a second client or replay actions.
          this.iframe.contentWindow?.postMessage(
            { type: 'gd-browser-ws-send', data: this.browserSessionReadyPacket },
            '*'
          );
        }
      }

      if (type === GD_CAPTURE_SCREEN) {
        void this.handleCaptureScreen(data);
      }

      if (type === GD_CHAT_READY) {
        this.chatReady = true;
        this.flushInitial();
      }

      // MODO MANUAL: o visitante fechou o chat.
      //
      // Mensagem própria em vez de inferir do payload de geometria, por fato de código: o launcher
      // posta `isEnabled: true` em TODAS as vistas, inclusive minimizado (ver
      // `launcherIframeGeometry`), então um `isEnabled === false` nunca chegaria dele. E no widget
      // o payload de montagem também vem com false, o que daria um fechamento fantasma no load.
      if (type === GD_MANUAL_CLOSED && this.triggerMode === 'manual') {
        if (this.conceal()) this.onManualClose?.();
      }

      switch (type) {
        case 'gd-request-parent-width':
          this.iframe?.contentWindow?.postMessage(
            { type: 'gd-parent-width', data: this.parentViewportPayload() },
            '*'
          );
          break;
        case 'gd-avatar-resize-reposition':
          this.handleResizeReposition(data);
          break;
        case 'gd-drag-start':
          this.beginFrameDrag(data);
          break;
        case 'gd-drag-move':
          this.moveFrameDrag(data);
          break;
        case 'gd-drag-end':
          this.endFrameDrag();
          break;
        case 'SHOW_SLIDE':
          this.showSlide(data);
          break;
        case 'HIDE_SLIDE':
          this.hideSlide();
          break;
        case 'gd-show-table':
          this.showTable(data);
          break;
        case 'gd-hide-table':
          this.hideTable();
          break;
        case 'gd-avatar-close':
          this.iframe?.remove();
          break;
      }
    });
  }

  /**
   * Fotografa a tela a pedido do player.
   *
   *   player ─gd-capture-screen{requestId}─▶ host
   *                                          captureScreen(): borra, clipa, rasteriza, restaura
   *                                          lê url/título/alertas/erros da página
   *   player ◀─gd-capture-screen-result───── { blob, maskedCount, pageContext } | { error }
   *
   * O Blob viaja por structured clone: virar data URL aqui inflaria 33% para nada.
   *
   * O guard de origem está no chamador (só mensagens do iframe do player chegam aqui): sem ele,
   * qualquer iframe de terceiro na página do cliente poderia pedir uma foto da tela dele.
   */
  private async handleCaptureScreen(data: any): Promise<void> {
    const requestId = typeof data?.requestId === 'string' ? data.requestId : null;
    if (!requestId) return;

    const urlBefore = window.location.href;
    const startedAt = Date.now();

    try {
      const shot = await captureScreen();

      // A SPA trocou de rota no meio da captura: a foto é de uma tela que o visitante não está
      // mais vendo. Mandar isso seria pior que não mandar nada.
      if (window.location.href !== urlBefore) {
        this.iframe?.contentWindow?.postMessage(
          {
            type: GD_CAPTURE_SCREEN_RESULT,
            data: { requestId, error: 'navigated', durationMs: Date.now() - startedAt }
          },
          '*'
        );
        return;
      }

      this.iframe?.contentWindow?.postMessage(
        {
          type: GD_CAPTURE_SCREEN_RESULT,
          data: {
            requestId,
            blob: shot.blob,
            width: shot.width,
            height: shot.height,
            maskedCount: shot.maskedCount,
            durationMs: Date.now() - startedAt,
            pageContext: {
              url: window.location.href,
              pageTitle: document.title,
              viewport: { width: window.innerWidth, height: window.innerHeight },
              alerts: readVisibleAlerts(),
              ...readPageErrorContext()
            }
          }
        },
        '*'
      );
    } catch (err) {
      const code = err instanceof ScreenCaptureError ? err.code : 'capture_failed';
      this.iframe?.contentWindow?.postMessage(
        {
          type: GD_CAPTURE_SCREEN_RESULT,
          data: { requestId, error: code, durationMs: Date.now() - startedAt }
        },
        '*'
      );
    }
  }

  private handleResizeReposition(data: any): void {
    if (!this.iframe || !this.backdrop) return;

    // O primeiro payload do player é o sinal de que ele montou e escuta. Um `open()` que chegou
    // antes disso ficou pendente — é aqui que ele é descarregado.
    this.markPlayerReady();

    // FORA do rAF abaixo: isto é escrituração, não estilo. O arrastar pode começar antes do próximo
    // quadro, e é a única descrição do widget que sobra depois que a moldura virou tamanho
    // constante — se chegar atrasada, o gesto mira a faixa inteira.
    this.collapsedVisual =
      data.visual && typeof data.visual.width === 'number'
        ? {
            width: data.visual.width,
            height: data.visual.height,
            anchorTop: data.top === '0',
            anchorLeft: data.left === '0'
          }
        : null;

    requestAnimationFrame(() => {
      const style = this.iframe!.style;

      // ÂNCORA ANTES DE TAMANHO. Um gesto de arrastar reescreve a moldura para `left/top` em pixels
      // e nunca a devolve para a ancoragem por canto (ver `beginFrameDrag`), então o payload chega
      // para desfazer isso — `left: auto` + `right: 0`. Medido numa página real: a moldura estava em
      // `left: 1786px` no canto superior direito. Aplicando o tamanho primeiro, existe um instante em
      // que ela já tem a largura nova ancorada pela borda velha: 450px começando em 1786px terminam
      // fora da tela. Reancorar primeiro e crescer depois nunca produz essa caixa.
      if (data.left !== undefined) style.left = data.left;
      if (data.right !== undefined) style.right = data.right;
      if (data.top !== undefined) style.top = data.top;
      if (data.bottom !== undefined) style.bottom = data.bottom;
      if (data.transform !== undefined) style.transform = data.transform;

      if (data.width !== undefined) style.width = data.width;
      if (data.height !== undefined) style.height = data.height;

      // O recorte é o que faz a moldura recolhida PARECER pequena sem mudar de caixa. Mantê-la do
      // mesmo tamanho nos dois estados é o que impede a bolha de aparecer um quadro deslocada: sem
      // origem nova, o compositor não tem onde desenhar a textura antiga fora do lugar. E `clip-path`
      // recorta o hit-testing junto com o pixel, então a área invisível não rouba clique da página.
      if (data.clip !== undefined) style.clipPath = data.clip;

      if (data.darkenBackground === true) {
        this.backdrop!.style.display = 'block';
      } else if (data.darkenBackground === false) {
        this.backdrop!.style.display = 'none';
      }

      style.opacity = '1';

      requestAnimationFrame(() => {
        const payload: Record<string, unknown> = {};
        if (typeof data?.isEnabled === 'boolean') {
          payload.isEnabled = data.isEnabled;
        }
        if (data?.gdHostCollapseIntent === true) {
          payload.gdHostCollapseIntent = true;
        }
        if (Object.keys(payload).length === 0) return;
        this.iframe?.contentWindow?.postMessage(
          { type: 'gd-avatar-resize-reposition', data: payload },
          '*'
        );
      });
    });
  }

  // Dragging the launcher moves the FRAME, and the HOST runs the whole gesture.
  //
  // Two decisions, both learned the hard way.
  //
  // The frame moves instead of growing. Growing it to the viewport so the player could paint the
  // box anywhere meant a resize crossing the document boundary mid-gesture, and for a frame or two
  // the player drew against a frame that no longer matched it.
  //
  // And no position is ever accumulated. The player only sees coordinates relative to its own
  // frame, which is the very thing being moved — so an event generated before a move landed but
  // delivered after it describes the old position, and adding deltas made the launcher shake.
  // Every position here is derived from where the gesture STARTED, so the same pointer position
  // always yields the same frame position and a late or repeated event changes nothing.
  //
  // That property is also what lets two sources feed one gesture: the player forwards what it
  // receives (in screen coordinates, which the frame's movement cannot invalidate) and this
  // document listens for what reaches the page. Which of the two fires depends on where the cursor
  // is and on browser behaviour not worth predicting, and they cannot disagree.
  private frameDrag: {
    /** Where the frame was when the gesture began; every position is derived from this. */
    left0: number;
    top0: number;
    /** The pointer's screen position at the same instant. */
    screenX0: number;
    screenY0: number;
    width: number;
    height: number;
    /**
     * A caixa VISÍVEL dentro da moldura, e o deslocamento dela em relação ao canto da moldura.
     *
     * Quando o widget está recolhido a moldura tem a mesma caixa da expandida e um `clip-path` expõe
     * só o canto — é isso que impede a bolha de aparecer um quadro deslocada ao abrir. Mas o arrastar
     * pergunta "onde está o widget" para escolher o destino, e a moldura deixou de ser essa resposta:
     * lida crua, ela dá o centro da faixa de 450×100vh e o destaque cai sempre no destino errado.
     */
    visualDx: number;
    visualDy: number;
    visualWidth: number;
    visualHeight: number;
    /** Screen pixels to page pixels. Measured once, from the first real movement. */
    scale: number | null;
    /** Where the pointer grabbed the frame — the spot kept under the cursor. */
    grabX: number;
    grabY: number;
    left: number;
    top: number;
    moved: boolean;
    /** Six destinations for the launcher bar, four corners for the floating widget. */
    destinations: 'launcher' | 'widget';
    onHostPointer: (event: PointerEvent) => void;
    onHostRelease: () => void;
  } | null = null;

  // Below this the gesture is a click that wobbled — and it is also too small to measure the screen
  // scale from without the rounding swamping it, so both wait for the same amount of travel.
  private static readonly DRAG_THRESHOLD_PX = 4;

  /**
   * Onde a parte VISÍVEL do widget está dentro da moldura.
   *
   * Sem recorte a resposta é a moldura inteira. Com recorte (estado recolhido) é o canto que ele
   * expõe — e qual canto sai da própria ancoragem que o último payload pediu.
   */
  private visualInset(rect: DOMRect): { dx: number; dy: number; width: number; height: number } {
    const v = this.collapsedVisual;
    if (!v) return { dx: 0, dy: 0, width: rect.width, height: rect.height };
    return {
      dx: v.anchorLeft ? 0 : Math.max(0, rect.width - v.width),
      dy: v.anchorTop ? 0 : Math.max(0, rect.height - v.height),
      width: Math.min(v.width, rect.width),
      height: Math.min(v.height, rect.height)
    };
  }

  private beginFrameDrag(data: any): void {
    if (!this.iframe || this.frameDrag) return;
    const rect = this.iframe.getBoundingClientRect();
    const visual = this.visualInset(rect);
    const onHostPointer = (event: PointerEvent) => this.moveFrameDragFromHost(event);
    const onHostRelease = () => this.endFrameDrag();
    this.frameDrag = {
      left0: rect.left,
      top0: rect.top,
      screenX0: Number(data?.screenX) || 0,
      screenY0: Number(data?.screenY) || 0,
      width: rect.width,
      height: rect.height,
      visualDx: visual.dx,
      visualDy: visual.dy,
      visualWidth: visual.width,
      visualHeight: visual.height,
      scale: null,
      grabX: Number(data?.grabX) || 0,
      grabY: Number(data?.grabY) || 0,
      left: rect.left,
      top: rect.top,
      moved: false,
      destinations: data?.destinations === 'widget' ? 'widget' : 'launcher',
      onHostPointer,
      onHostRelease
    };

    // Two sources feed the same gesture, because which one fires depends on where the cursor is and
    // on browser behaviour nobody should have to predict: the player forwards what it receives
    // while the pointer is over the frame, and these listeners catch it once it is over the page.
    // Doubling up is harmless precisely because the position is derived from the gesture's origin
    // rather than accumulated — the same pointer position yields the same frame position no matter
    // how many times, or from which side, it arrives.
    document.addEventListener('pointermove', onHostPointer, true);
    document.addEventListener('pointerup', onHostRelease, true);
    document.addEventListener('pointercancel', onHostRelease, true);

    // The destinations are NOT drawn here. A press is not yet a drag: the bubble is a button as
    // much as it is a handle, and someone reaching for it to open the chat would get the whole page
    // marked up for a gesture they never made. They appear on the first real movement instead —
    // which is also the first moment they mean anything, since they preview where it will land.

    // Re-express the current position as plain left/top before touching it. The docked bar is
    // centred with a percentage and a transform, which cannot be nudged in pixels; measuring first
    // makes the switch exact, so nothing moves on screen.
    Object.assign(this.iframe.style, {
      left: `${rect.left}px`,
      top: `${rect.top}px`,
      right: 'auto',
      bottom: 'auto',
      transform: 'none'
    });
  }

  private moveFrameDrag(data: any): void {
    const drag = this.frameDrag;
    if (!drag || !this.iframe) return;
    const screenDx = (Number(data?.screenX) || 0) - drag.screenX0;
    const screenDy = (Number(data?.screenY) || 0) - drag.screenY0;

    if (drag.scale === null) {
      const travelled = Math.hypot(screenDx, screenDy);
      if (travelled < BotWidgetManager.DRAG_THRESHOLD_PX) return;
      // Screen pixels and page pixels are the same thing only at 100% browser zoom. The frame has
      // not moved yet, so the pointer's travel inside it IS its travel across the page — one
      // sample of both makes them comparable for the rest of the gesture, without having to guess
      // the zoom from devicePixelRatio (which also carries the display's own scaling).
      const pageTravel = Math.hypot(
        (Number(data?.clientX) || 0) - (Number(data?.grabX) || 0),
        (Number(data?.clientY) || 0) - (Number(data?.grabY) || 0)
      );
      drag.scale = pageTravel > 0 ? pageTravel / travelled : 1;
      drag.moved = true;
    }

    // Always derived from where the gesture STARTED, never accumulated. The same pointer position
    // therefore always yields the same frame position: a late or repeated event produces what it
    // always would have, so nothing can drift and nothing can oscillate.
    this.applyFrameDrag(drag.left0 + screenDx * drag.scale, drag.top0 + screenDy * drag.scale);
  }

  // The host sees the pointer in page coordinates, which is the frame's own coordinate system —
  // exact, and with no screen-to-page scale to calibrate. Same clamp, same idempotence.
  private moveFrameDragFromHost(event: PointerEvent): void {
    const drag = this.frameDrag;
    if (!drag || !this.iframe) return;
    const left = event.clientX - drag.grabX;
    const top = event.clientY - drag.grabY;
    if (
      !drag.moved &&
      Math.hypot(left - drag.left0, top - drag.top0) < BotWidgetManager.DRAG_THRESHOLD_PX
    ) {
      return;
    }
    drag.moved = true;
    this.applyFrameDrag(left, top);
  }

  private applyFrameDrag(left: number, top: number): void {
    const drag = this.frameDrag;
    if (!drag || !this.iframe) return;
    // Calibration closes the moment the frame moves, whichever source moved it. The screen-to-page
    // sample is only valid while the frame is still at rest: taken afterwards, the pointer has
    // returned to its grab point and the measured page travel collapses towards zero, which would
    // leave the rest of the gesture crawling. When the host's own events are driving, they are in
    // page coordinates and need no scale at all.
    if (drag.scale === null) drag.scale = 1;
    // O limite é sobre o que se VÊ, não sobre a moldura — e por isso a moldura pode passar da borda.
    //
    // Recolhida, ela tem a caixa da expandida (450×100vh) com um recorte expondo só um canto. Limitar
    // a moldura à viewport, como era antes, prende `top` em zero quando a altura dela é a da tela: o
    // gesto só se movia na horizontal, e mesmo essa não alcançava a outra ponta. O que precisa caber
    // na tela é a bolha; o resto da moldura é transparente e recortado, então sair da borda não
    // mostra nem intercepta nada.
    const minLeft = -drag.visualDx;
    const maxLeft = window.innerWidth - drag.visualDx - drag.visualWidth;
    const minTop = -drag.visualDy;
    const maxTop = window.innerHeight - drag.visualDy - drag.visualHeight;
    drag.left = Math.min(Math.max(left, minLeft), Math.max(minLeft, maxLeft));
    drag.top = Math.min(Math.max(top, minTop), Math.max(minTop, maxTop));
    this.iframe.style.left = `${drag.left}px`;
    this.iframe.style.top = `${drag.top}px`;
    // First actual movement: now it is a drag, so the destinations earn their place on the page.
    // Idempotent, so every later move just re-highlights.
    this.showDragTargets();
    this.highlightDragTarget();
  }

  // The destinations, drawn dotted over the page while a drag is running. They live here because
  // they have to cover the whole page and the player's frame reaches only as far as itself.
  //
  // They are a PREVIEW, not the position: the frame keeps following the cursor and only settles on
  // release. Snapping live would make the launcher jump between destinations while the visitor is
  // still deciding.
  private dragTargets: HTMLDivElement | null = null;

  private showDragTargets(): void {
    const drag = this.frameDrag;
    if (!drag || this.dragTargets) return;
    const layer = document.createElement('div');
    layer.id = 'getdemo-drag-targets';
    Object.assign(layer.style, {
      position: 'fixed',
      inset: '0',
      // Under the frame being dragged, and never a hit-test target: these are a hint, and a hint
      // that swallows a click would be worse than no hint.
      zIndex: '9998',
      pointerEvents: 'none'
    });

    const slots = drag.destinations === 'widget' ? WIDGET_CORNERS : LAUNCHER_SLOTS;
    for (const slot of slots) {
      const target = document.createElement('div');
      target.dataset.slot = slot;
      // Each outline is the size of the thing being dragged, so the visitor sees where it lands
      // rather than a generic marker. A coisa arrastada é o que se VÊ — recolhido, a moldura tem a
      // caixa da expandida e só um canto dela aparece. Medir a moldura desenharia quatro retângulos
      // de 450×100vh, que é o que cobria meia página em vez de mostrar onde a bolha cai.
      Object.assign(target.style, {
        position: 'fixed',
        boxSizing: 'border-box',
        width: `${drag.visualWidth}px`,
        height: `${drag.visualHeight}px`,
        border: '2px dashed rgba(100, 116, 139, 0.55)',
        borderRadius: '18px',
        backgroundColor: 'rgba(100, 116, 139, 0.06)',
        transition: 'background-color 120ms ease, border-color 120ms ease',
        ...(drag.destinations === 'widget'
          ? widgetCornerCss(slot as never)
          : launcherSlotCss(slot as never))
      });
      layer.appendChild(target);
    }

    document.body.appendChild(layer);
    this.dragTargets = layer;
  }

  private highlightDragTarget(): void {
    const drag = this.frameDrag;
    if (!drag || !this.dragTargets) return;
    // O centro do que se VÊ, não o da moldura: recolhido, a moldura tem a caixa da expandida e só um
    // canto dela é visível. Medir a moldura crua daria sempre o centro da faixa, e o destino
    // escolhido seria o mesmo qualquer que fosse o gesto.
    const centre = {
      x: drag.left + drag.visualDx + drag.visualWidth / 2,
      y: drag.top + drag.visualDy + drag.visualHeight / 2
    };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    const nearest =
      drag.destinations === 'widget'
        ? nearestWidgetCorner(centre, viewport)
        : nearestLauncherSlot(centre, viewport);
    for (const node of Array.from(this.dragTargets.children) as HTMLDivElement[]) {
      const active = node.dataset.slot === nearest;
      node.style.borderColor = active ? 'rgba(59, 108, 246, 0.9)' : 'rgba(100, 116, 139, 0.55)';
      node.style.backgroundColor = active
        ? 'rgba(59, 108, 246, 0.12)'
        : 'rgba(100, 116, 139, 0.06)';
    }
  }

  private hideDragTargets(): void {
    this.dragTargets?.remove();
    this.dragTargets = null;
  }

  private endFrameDrag(): void {
    const drag = this.frameDrag;
    if (!drag) return;
    this.frameDrag = null;
    // Cleared before anything else can fail: an outline left on the customer's page after the
    // gesture is over is worse than never having drawn it.
    this.hideDragTargets();
    document.removeEventListener('pointermove', drag.onHostPointer, true);
    document.removeEventListener('pointerup', drag.onHostRelease, true);
    document.removeEventListener('pointercancel', drag.onHostRelease, true);
    // The frame follows the cursor for the whole gesture and only settles here. Resolving the
    // destination is the host's job because only it knows the page — and the answer is a name, so
    // the player stores a choice rather than a rectangle it would have to re-clamp forever after.
    // O centro do que se VÊ, não o da moldura: recolhido, a moldura tem a caixa da expandida e só um
    // canto dela é visível. Medir a moldura crua daria sempre o centro da faixa, e o destino
    // escolhido seria o mesmo qualquer que fosse o gesto.
    const centre = {
      x: drag.left + drag.visualDx + drag.visualWidth / 2,
      y: drag.top + drag.visualDy + drag.visualHeight / 2
    };
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    this.iframe?.contentWindow?.postMessage(
      {
        type: 'gd-drag-ended',
        data: {
          moved: drag.moved,
          slot:
            drag.destinations === 'widget'
              ? nearestWidgetCorner(centre, viewport)
              : nearestLauncherSlot(centre, viewport)
        }
      },
      '*'
    );
  }

  private ensureSlideOverlay(): void {
    if (this.slideOverlay && this.slideClose && this.slideContent) return;

    this.slideOverlay = document.createElement('div');
    this.slideOverlay.id = 'getdemo-slide-overlay';
    Object.assign(this.slideOverlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      background: 'rgba(0,0,0,0.95)',
      zIndex: '9999999',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center'
    });

    this.slideClose = document.createElement('button');
    this.slideClose.id = 'getdemo-slide-close';
    this.slideClose.innerHTML = '\u00d7';
    Object.assign(this.slideClose.style, {
      position: 'absolute',
      top: '20px',
      right: '20px',
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      border: 'none',
      background: 'rgba(255,255,255,0.2)',
      color: 'white',
      fontSize: '28px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: '10000000',
      transition: 'background 0.2s'
    });
    this.slideClose.onmouseover = e => {
      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.3)';
    };
    this.slideClose.onmouseout = e => {
      (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.2)';
    };

    this.slideContent = document.createElement('div');
    this.slideContent.id = 'getdemo-slide-content';
    Object.assign(this.slideContent.style, {
      maxWidth: '90vw',
      maxHeight: '90vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    });

    this.slideClose.onclick = () => this.hideSlide();
    this.slideOverlay.onclick = (e: MouseEvent) => {
      if (e.target === this.slideOverlay || e.target === this.slideClose) {
        this.hideSlide();
      }
    };

    this.slideOverlay.appendChild(this.slideClose);
    this.slideOverlay.appendChild(this.slideContent);
    document.body.appendChild(this.slideOverlay);
  }

  private showSlide(data: any): void {
    if (!document.body) {
      setTimeout(() => this.showSlide(data), 100);
      return;
    }

    this.ensureSlideOverlay();
    if (!this.slideContent || !this.slideOverlay) return;

    const { url, type, description } = data;
    this.slideContent.innerHTML = '';

    if (type === 'image') {
      const img = document.createElement('img');
      img.src = url;
      img.alt = description || 'Slide';
      Object.assign(img.style, {
        maxWidth: '100%',
        maxHeight: '90vh',
        objectFit: 'contain',
        borderRadius: '8px'
      });
      this.slideContent.appendChild(img);
    } else {
      const slideIframe = document.createElement('iframe');
      let finalUrl = url;

      if (type === 'demo') {
        try {
          const urlObj = new URL(url);
          if (urlObj.searchParams.get('autoplay') !== 'true') {
            urlObj.searchParams.set('autoplay', 'true');
          }
          if (
            !urlObj.searchParams.get('autoplayDelay') ||
            urlObj.searchParams.get('autoplayDelay') === '0'
          ) {
            urlObj.searchParams.set('autoplayDelay', '3');
          }
          urlObj.searchParams.set('avatarIframe', 'true');
          finalUrl = urlObj.toString();
        } catch {
          const separator = url.includes('?') ? '&' : '?';
          finalUrl = url + separator + 'autoplay=true&autoplayDelay=3&avatarIframe=true';
        }
      }

      slideIframe.src = finalUrl;
      slideIframe.title = description || 'Interactive Content';
      Object.assign(slideIframe.style, {
        width: '90vw',
        height: '90vh',
        border: 'none',
        borderRadius: '8px'
      });
      slideIframe.setAttribute('allowFullScreen', 'true');
      this.slideContent.appendChild(slideIframe);
    }

    this.slideOverlay.style.display = 'flex';
  }

  private hideSlide(): void {
    if (this.slideOverlay) {
      this.slideOverlay.style.display = 'none';
      if (this.slideContent) this.slideContent.innerHTML = '';
    }

    if (this.iframe?.contentWindow) {
      this.iframe.contentWindow.postMessage({ type: 'HIDE_SLIDE' }, '*');
    }
  }

  // Fullscreen data-table overlay, rendered on the HOST page (outside the widget iframe). The
  // player posts `gd-show-table` with pre-rendered, inline-styled HTML; we just inject + show it.
  // Because nothing about the iframe is resized, opening is instant and the chat is untouched —
  // unlike an in-iframe modal, which needs the iframe grown to fullscreen first (flash + delay).
  private ensureTableOverlay(): void {
    if (this.tableOverlay && this.tableContent) return;

    // Scoped scrollbar styling for the overlay's inner scroller — thin, rounded, and clearly
    // visible so it obviously reads as scrollable (WebKit), with a Firefox fallback. Scoped to the
    // overlay id + scroller class so it never leaks into the host page. Injected once.
    if (!document.getElementById('getdemo-table-overlay-style')) {
      const style = document.createElement('style');
      style.id = 'getdemo-table-overlay-style';
      style.textContent =
        '#getdemo-table-overlay .gd-table-scroller{scrollbar-width:thin;scrollbar-color:rgba(255,255,255,0.28) transparent;}' +
        '#getdemo-table-overlay .gd-table-scroller::-webkit-scrollbar{width:10px;height:10px;}' +
        '#getdemo-table-overlay .gd-table-scroller::-webkit-scrollbar-track{background:transparent;}' +
        '#getdemo-table-overlay .gd-table-scroller::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.24);border-radius:8px;border:2px solid transparent;background-clip:padding-box;}' +
        '#getdemo-table-overlay .gd-table-scroller::-webkit-scrollbar-thumb:hover{background:rgba(255,255,255,0.4);border:2px solid transparent;background-clip:padding-box;}' +
        '#getdemo-table-overlay .gd-table-scroller::-webkit-scrollbar-corner{background:transparent;}';
      (document.head || document.documentElement).appendChild(style);
    }

    const overlay = document.createElement('div');
    overlay.id = 'getdemo-table-overlay';
    Object.assign(overlay.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '100vw',
      height: '100vh',
      boxSizing: 'border-box',
      padding: '24px',
      background: 'rgba(0,0,0,0.82)',
      zIndex: '9999999',
      display: 'none',
      alignItems: 'center',
      justifyContent: 'center',
      opacity: '0',
      transition: 'opacity 200ms ease-out'
    });

    // The rounded card only CLIPS (overflow:hidden) — it never scrolls. This keeps the native
    // scrollbars off the border-radius, so they no longer cut across the rounded corners. The
    // actual scrolling happens in the inner `.gd-table-scroller` below.
    const card = document.createElement('div');
    Object.assign(card.style, {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: 'min(1200px, 94vw)',
      maxHeight: '88vh',
      overflow: 'hidden',
      background: '#2b2b2d',
      color: '#ececec',
      borderRadius: '12px',
      boxShadow: '0 10px 40px rgba(0,0,0,0.5)'
    });

    // Non-scrolling top bar that OWNS the close button. Keeping the close in its own row (instead
    // of absolutely positioned over the scroller) guarantees it never overlaps the table header or
    // the common-fields line — in any scroll position, including horizontal scroll where the last
    // column would otherwise slide under it.
    const topbar = document.createElement('div');
    Object.assign(topbar.style, {
      flex: '0 0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      padding: '10px 10px 0'
    });

    const close = document.createElement('button');
    close.type = 'button';
    // SVG X, not the "×" glyph: a text glyph carries its own font metrics, so the multiplication
    // sign rides high and sits visually off-centre inside the round button even when its text box
    // is centred. A symmetric SVG icon (drawn around the viewBox centre) centres exactly, like the
    // in-chat close button. stroke=currentColor picks up the button's white color.
    close.innerHTML =
      '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="2.5" stroke-linecap="round" aria-hidden="true">' +
      '<path d="M6 6 L18 18 M18 6 L6 18" /></svg>';
    close.setAttribute('aria-label', 'Fechar');
    Object.assign(close.style, {
      flex: '0 0 auto',
      width: '34px',
      height: '34px',
      // Injected into the HOST page, so a global `button { padding/box-sizing }` rule there leaks in
      // and shifts the icon. Reset the box model and centre the icon with flex so it stays centred
      // regardless of host CSS.
      boxSizing: 'border-box',
      margin: '0',
      padding: '0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      border: 'none',
      background: 'rgba(255,255,255,0.14)',
      color: '#fff',
      cursor: 'pointer'
    });
    close.onclick = () => this.hideTable();

    // Inner scroll region — owns BOTH scroll axes. Separate from the rounded card so the sticky
    // table header sticks to the true top of a plain (non-rounded) scrollport, which fixes the
    // header-bleeds-over-rows paint glitch that `border-radius + overflow:auto + sticky` caused.
    const scroller = document.createElement('div');
    scroller.className = 'gd-table-scroller';
    Object.assign(scroller.style, {
      flex: '1 1 auto',
      minHeight: '0',
      // Fallback scroll bound (topbar is ~44px: 10px pad + 34px button). Redundant with the flex
      // chain on well-behaved pages, but if a host's global CSS neutralizes flex (e.g.
      // `div{display:block!important}`) this keeps the scroller bounded so rows stay reachable
      // instead of being clipped by the card's overflow:hidden with no scrollbar.
      maxHeight: 'calc(88vh - 44px)',
      overflow: 'auto',
      // No TOP padding: the sticky table header must sit flush to the scrollport top, otherwise
      // rows bleed through the top-padding strip as they scroll up behind it. The top gap is
      // instead supplied by the content's own padding-top, which scrolls away with the content.
      padding: '0 20px 20px'
    });

    const content = document.createElement('div');
    content.style.paddingTop = '20px';

    topbar.appendChild(close);
    scroller.appendChild(content);
    card.appendChild(topbar);
    card.appendChild(scroller);
    overlay.appendChild(card);
    overlay.onclick = (e: MouseEvent) => {
      if (e.target === overlay) this.hideTable();
    };

    this.tableKeyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') this.hideTable();
    };

    document.body.appendChild(overlay);
    this.tableOverlay = overlay;
    this.tableContent = content;
  }

  private showTable(data: any): void {
    if (!document.body) {
      setTimeout(() => this.showTable(data), 50);
      return;
    }
    this.ensureTableOverlay();
    if (!this.tableOverlay || !this.tableContent) return;

    this.tableContent.innerHTML = typeof data?.html === 'string' ? data.html : '';
    this.tableOverlay.style.display = 'flex';
    // Force a reflow so the opacity transition runs from 0 (instead of jumping to 1).
    void this.tableOverlay.offsetHeight;
    this.tableOverlay.style.opacity = '1';

    if (this.tableKeyHandler) {
      document.addEventListener('keydown', this.tableKeyHandler);
    }
  }

  private hideTable(): void {
    if (!this.tableOverlay) return;
    if (this.tableKeyHandler) {
      document.removeEventListener('keydown', this.tableKeyHandler);
    }
    const overlay = this.tableOverlay;
    overlay.style.opacity = '0';
    setTimeout(() => {
      overlay.style.display = 'none';
      if (this.tableContent) this.tableContent.innerHTML = '';
    }, 200);
  }

  private getOverlayIframe(): HTMLIFrameElement | null {
    return this.slideContent?.querySelector('iframe') || null;
  }

  private handleOverlayIframeMessage(event: MessageEvent): void {
    const overlayFrame = this.getOverlayIframe();
    if (!overlayFrame || event.source !== overlayFrame.contentWindow) return;

    const { type } = event.data || {};

    if (type === 'HIDE_SLIDE') {
      this.hideSlide();
      if (overlayFrame.contentWindow) {
        overlayFrame.contentWindow.postMessage({ type: 'HIDE_SLIDE' }, '*');
      }
    } else if (type === 'DEMO_TOOLTIP_SHOWN' || type === 'TOOLTIP_SENT_TO_AVATAR') {
      if (this.iframe?.contentWindow) {
        this.iframe.contentWindow.postMessage(event.data, '*');
      }
    }
  }
}

declare global {
  interface Window {
    GetDemoBrowser?: {
      init(config: any): void;
      initWithTransport(config: { transport: any; debug?: boolean }): void;
      initAsBridge(iframeId: string): void;
      destroy(): void;
      getState(): any;
      debug(): void;
      captureScreenshot?(options?: {
        area?: 'viewport' | 'document';
        exclude?: string[];
        maxWidth?: number;
        format?: 'png' | 'jpeg';
        quality?: number;
      }): Promise<{ dataUrl: string; width: number; height: number }>;
    };
  }
}
